/**
 * Currency Helper - Centralized monetary value handling
 * All monetary values are stored and transmitted in minor units (cents)
 * Example: R$ 12,90 = 1290 cents
 * 
 * Usage:
 *   import { toMinor, toMajor, formatCurrency } from './currency';
 *   
 *   // Convert R$ 12.90 to cents: 1290
 *   const cents = toMinor(12.90);
 *   
 *   // Convert 1290 cents to R$ 12.90
 *   const reais = toMajor(1290);
 *   
 *   // Format: "R$ 12,90"
 *   const formatted = formatCurrency(1290, { currency: 'BRL' });
 */

// Currency configurations
export const CURRENCY_CONFIG = {
  BRL: {
    symbol: 'R$',
    decimalPlaces: 2,
    decimalSeparator: ',',
    thousandsSeparator: '.',
    locale: 'pt-BR',
  },
  USD: {
    symbol: '$',
    decimalPlaces: 2,
    decimalSeparator: '.',
    thousandsSeparator: ',',
    locale: 'en-US',
  },
  EUR: {
    symbol: '€',
    decimalPlaces: 2,
    decimalSeparator: ',',
    thousandsSeparator: '.',
    locale: 'de-DE',
  },
};

/**
 * Get the multiplier based on decimal places (10^decimalPlaces)
 * @param {string} currency - Currency code
 * @returns {number} Multiplier (e.g., 100 for 2 decimal places)
 */
export function getMultiplier(currency = 'BRL') {
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.BRL;
  return Math.pow(10, config.decimalPlaces);
}

/**
 * Convert a major unit value (e.g., 12.90) to minor units (cents, e.g., 1290)
 * @param {number|string|null|undefined} value - Value in major units
 * @param {string} currency - Currency code (default: BRL)
 * @returns {number} Integer value in minor units (cents)
 * 
 * @example
 * toMinor(12.90) // => 1290
 * toMinor(100) // => 10000
 * toMinor("12,90") // => 1290
 * toMinor(null) // => 0
 */
export function toMinor(value, currency = 'BRL') {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  
  // Handle string input (may have comma as decimal separator)
  if (typeof value === 'string') {
    // Remove currency symbols and spaces
    let cleaned = value.replace(/[R$€\s]/g, '');
    
    const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.BRL;
    
    // Handle Brazilian format: 1.234,56 -> 1234.56
    if (config.decimalSeparator === ',') {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      cleaned = cleaned.replace(/,/g, '');
    }
    
    value = parseFloat(cleaned);
    if (isNaN(value)) return 0;
  }
  
  const multiplier = getMultiplier(currency);
  return Math.round(value * multiplier);
}

/**
 * Convert minor units (cents) to major units (e.g., 1290 -> 12.90)
 * @param {number|null|undefined} valueMinor - Value in minor units (cents)
 * @param {string} currency - Currency code (default: BRL)
 * @returns {number} Float value in major units
 * 
 * @example
 * toMajor(1290) // => 12.90
 * toMajor(10000) // => 100.00
 * toMajor(null) // => 0
 */
export function toMajor(valueMinor, currency = 'BRL') {
  if (valueMinor === null || valueMinor === undefined) {
    return 0;
  }
  
  const multiplier = getMultiplier(currency);
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.BRL;
  const result = valueMinor / multiplier;
  
  // Round to correct decimal places
  const factor = Math.pow(10, config.decimalPlaces);
  return Math.round(result * factor) / factor;
}

/**
 * Format minor units as a currency string
 * @param {number|null|undefined} valueMinor - Value in minor units (cents)
 * @param {Object} options - Formatting options
 * @param {string} options.currency - Currency code (default: BRL)
 * @param {boolean} options.showSymbol - Whether to include currency symbol (default: true)
 * @param {boolean} options.useLocale - Use Intl.NumberFormat (default: true)
 * @returns {string} Formatted currency string
 * 
 * @example
 * formatCurrency(1290) // => "R$ 12,90"
 * formatCurrency(1290, { showSymbol: false }) // => "12,90"
 * formatCurrency(100000) // => "R$ 1.000,00"
 */
export function formatCurrency(valueMinor, options = {}) {
  const { 
    currency = 'BRL', 
    showSymbol = true,
    useLocale = true 
  } = options;
  
  if (valueMinor === null || valueMinor === undefined) {
    valueMinor = 0;
  }
  
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.BRL;
  const majorValue = toMajor(valueMinor, currency);
  
  if (useLocale) {
    try {
      const formatted = new Intl.NumberFormat(config.locale, {
        style: showSymbol ? 'currency' : 'decimal',
        currency: currency,
        minimumFractionDigits: config.decimalPlaces,
        maximumFractionDigits: config.decimalPlaces,
      }).format(majorValue);
      return formatted;
    } catch (e) {
      // Fallback to manual formatting
    }
  }
  
  // Manual formatting fallback
  const fixed = Math.abs(majorValue).toFixed(config.decimalPlaces);
  const [intPart, decPart] = fixed.split('.');
  
  // Add thousands separator
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, config.thousandsSeparator);
  
  const sign = majorValue < 0 ? '-' : '';
  const formatted = `${sign}${formattedInt}${config.decimalSeparator}${decPart}`;
  
  if (showSymbol) {
    return `${config.symbol} ${formatted}`;
  }
  return formatted;
}

/**
 * Parse a formatted currency string to minor units
 * @param {string} value - Formatted currency string
 * @param {string} currency - Currency code (default: BRL)
 * @returns {number} Integer value in minor units
 * 
 * @example
 * parseCurrencyString("R$ 12,90") // => 1290
 * parseCurrencyString("1.234,56") // => 123456
 */
export function parseCurrencyString(value, currency = 'BRL') {
  return toMinor(value, currency);
}

/**
 * Validate a minor unit value
 * @param {number} value - Value in minor units to validate
 * @param {Object} options - Validation options
 * @param {number} options.minValue - Minimum allowed value
 * @param {number} options.maxValue - Maximum allowed value
 * @returns {boolean} True if valid
 */
export function validateMinorValue(value, options = {}) {
  const { minValue, maxValue } = options;
  
  if (!Number.isInteger(value)) {
    return false;
  }
  
  if (minValue !== undefined && value < minValue) {
    return false;
  }
  
  if (maxValue !== undefined && value > maxValue) {
    return false;
  }
  
  return true;
}

/**
 * Create a currency input handler that works with minor units
 * Useful for controlled inputs
 * @param {Function} setValueMinor - State setter for minor value
 * @param {string} currency - Currency code
 * @returns {Object} Input handlers { value, onChange, onBlur }
 */
export function useCurrencyInput(valueMinor, setValueMinor, currency = 'BRL') {
  const displayValue = toMajor(valueMinor, currency);
  
  const handleValueChange = (majorValue) => {
    if (majorValue === null || majorValue === undefined) {
      setValueMinor(0);
    } else {
      setValueMinor(toMinor(majorValue, currency));
    }
  };
  
  return {
    value: displayValue,
    onValueChange: handleValueChange,
  };
}

export default {
  CURRENCY_CONFIG,
  getMultiplier,
  toMinor,
  toMajor,
  formatCurrency,
  parseCurrencyString,
  validateMinorValue,
  useCurrencyInput,
};
